// Module 12 public surface. This slice owns the typed connected-integration access seam, the typed
// provider registry, the account maps, the connection flows, the Connections SETTINGS REGISTRATION
// and — since ORP-18698 — the Connections page itself. The 34 per-connector modals follow
// additively under §17 (ORP-18699/18700/18701), each filling in its own registry `configComponent`.
//
// The page is normally reached through the settings child route this module registers. Its public
// component export below exists only so the app composition root can wrap it with a cross-feature
// outcome host; module 22 remains forbidden from importing registrant components directly.
import { defineAsyncComponent } from 'vue'

export { useIntegrations } from './composables/useIntegrations'
export type { UseIntegrations } from './composables/useIntegrations'
// The Meta checkpoint (ORP-26180/26183). The app banner (29) and the Health Checks card (21) read
// it here so neither re-derives whether it is showing, who it names, or what a verify answered.
export { useMetaCheckpoint } from './composables/useMetaCheckpoint'
export type { UseMetaCheckpoint } from './composables/useMetaCheckpoint'
export { META_CHECKPOINT_DIAGNOSTIC_LABEL } from './service/metaCheckpoint'
export type { MetaCheckpointVerifyOutcome } from './service/metaCheckpoint'
export { registerIntegrationBootstrapHydrators } from './flows/bootstrapHydrators'
// The surface half of the same two edges: the hydrators reset the store, this closes the dialogs
// that were open over it (ORP-20384).
export { registerIntegrationsLifecycle } from './lifecycle/registerIntegrationsLifecycle'

// The typed provider registry (contract §4/§5, ORP-18696). Only the TYPES are public: the registry
// data has no cross-module consumer yet, and the two that are coming — the store port (ORP-18697)
// and the Connections page (ORP-18698) — live inside this feature and import it directly. 08's
// channel gating is the first outside reader; its exports land with it.
export type { AuthFamily, IntegrationCategory, IntegrationProvider } from './models/providers.types'

// Get Started provider-progress facade (ORP-20113). Module 29 consumes only these exports; the
// normalization, registry lookup, safe retry-path validation and shell event parser stay private.
export { default as GetStartedPlatformProgress } from './components/GetStartedPlatformProgress.vue'
export {
  createGetStartedPlatformProgress,
  registerGetStartedProgressEventBus,
  useGetStartedPlatformProgress,
} from './composables/useGetStartedPlatformProgress'
export type {
  GetStartedProgressEventBus,
  GetStartedProgressSource,
  UseGetStartedPlatformProgress,
} from './composables/useGetStartedPlatformProgress'
export type {
  GetStartedPlatformProgressItem,
  GetStartedPlatformStage,
  GetStartedPlatformState,
} from './models/getStartedProgress.types'

// Post-connect refresh (ORP-18697). NOT the boot/switch path: `'integrations'` is one of 07's
// workspace-level sections, so `SelectWorkspaceFlow` already resets and re-dispatches it — calling
// this from 07 would double-fetch. See `service/integrationService.ts`.
export { refreshAll as refreshIntegrations } from './service/integrationService'
export type { RefreshOutcome } from './service/integrationService'
export type { IntegrationAccountDto, WorkspaceIntegrationMap } from './models/integration.types'
export type { ProviderConnectedEvent } from './models/googleConnector.types'
export { default as OnboardingConnectorSurface } from './components/onboarding/OnboardingConnectorSurface.vue'
export type {
  OnboardingConnectionResult,
  ShopifyOnboardingConnectionResult,
  ShopifyOnboardingDestinationResolver,
} from './models/onboardingConnection.types'
export {
  PROVIDER_CONNECTED_EVENT,
  subscribeProviderConnected,
} from './service/providerConnectedBus'
export type { ProviderConnectedListener } from './service/providerConnectedBus'
// App composition may wrap the route surface with cross-feature hosts. Module 12 still imports no
// follow-up owner; the app is the only layer allowed to know both sides of that seam.
//
// `ConnectionsPage` is a lazy wrapper here, not a static re-export (ORP-22679, contract 05 §11):
// `app/bootstrap.ts` imports this barrel statically for its lifecycle registrants, and a plain
// `export {default} from './X.vue'` line pulls the WHOLE module — script, styles, and its own
// dependency graph — into that static chunk regardless of whether the static importer uses the
// binding (Rollup cannot prove an SFC re-export is side-effect free). `ConnectionsSettingsHost`
// (the sole consumer, `app/shell/components/ConnectionsSettingsHost.vue`) is itself only ever
// loaded lazily (`bootstrap.ts`'s `connectionsComponent: () => import(...)`), so the component
// stays reachable — just behind its own dynamic import instead of the barrel's static one.
export const ConnectionsPage = defineAsyncComponent(
  () => import('./components/ConnectionsPage.vue'),
)
// The BigQuery query byte limit row (ORP-25955), hosted by module 30's Definitions pane. Lazy for
// the same reason as `ConnectionsPage`: this barrel is in the boot chunk, the row is not.
// Onboarding step 3's platform list (v3 `OnboardingConnectPlatforms.vue`). Lazy for the same reason
// as `ConnectionsPage`: the wizard is a blank-layout route, not boot-chunk chrome.
export const OnboardingPlatformList = defineAsyncComponent(
  () => import('./components/onboarding/OnboardingPlatformList.vue'),
)
export const BigQueryQueryByteLimitRow = defineAsyncComponent(
  () => import('./components/bigquery/QueryByteLimitRow.vue'),
)
// Singular's daily row limit row (ORP-25841), hosted by the same pane and lazy for the same reason.
export const SingularDailyRowBudgetRow = defineAsyncComponent(
  () => import('./components/singular/SingularDailyRowBudgetRow.vue'),
)

// ══ THE SETTINGS REGISTRATION (contract 22 §8; ORP-18893 precondition 5) ══
// `app/bootstrap.ts` — and nothing else — calls this, handing over 22's two register functions. The
// route records are NOT exported separately: they are meaningless outside the `/settings` parent
// they are appended to, and 05 must not compose them into the app layout (see `routes.ts`).
export { installIntegrationsSettingsNav } from './install'
export type { IntegrationsSettingsOptions, IntegrationsSettingsRegistrar } from './install'
export { registerIntegrationsMessages } from './i18n'
// Public navigation target only. Route records stay private because they are meaningful solely as
// children registered into module 22's `/settings` parent.
export { CONNECTIONS_ROUTE, SLACK_SETTINGS_ROUTE } from './routeTarget'

// Native MCP signed-handoff surface (ORP-20043). App composition consumes the standalone connect
// record. The in-app connections screen and Get Started's MCP card are gone: setup lives on
// mcp.orphex.co (ORP-27468), and `/settings/mcp/connections` only redirects home.
export { mcpRoutes } from './routes'
export { MCP_CONNECT_ROUTE } from './mcpRouteTargets'
export { McpConnectionsApi, MCP_CONNECTIONS_PATH } from './api/McpConnectionsApi'
export type {
  McpBoundWorkspace,
  McpConnection,
  McpConnectionListResponse,
  McpSessionConnectPayload,
  McpSessionConnectResponse,
  McpSessionPreview,
} from './models/mcp.types'

// 12's own gates, built on 07's `useRbac()`. Exported because the settings entry's `visible()`
// predicate belongs to 12 (contract 22 §5) and callers outside this module — the nav, a future
// connector CTA — must read the rule rather than re-derive it.
export { useIntegrationsRbac } from './composables/useIntegrationsRbac'
export type { UseIntegrationsRbac } from './composables/useIntegrationsRbac'

// Cross-module provider-account and BigQuery browsing seam (ORP-18857). Integrations owns these
// provider resources; Data Connectors, Data Export and Scheduled Queries consume only this barrel.
export { useProviderAccounts } from './composables/useProviderAccounts'
export type { UseProviderAccounts } from './composables/useProviderAccounts'
export { useBigQueryBrowser } from './composables/useBigQueryBrowser'
export type { UseBigQueryBrowser } from './composables/useBigQueryBrowser'
export type {
  BigQueryDataset,
  BigQueryTable,
  ProviderAccount,
  ProviderAccountParams,
} from './models/integrationResources.types'

// ══ THE SLACK SURFACE (ORP-19904) ══
// Unlike the settings records above, these ARE exported: they are composed by 05 into the app's
// route table — `slackLinkRoutes` as a standalone absolute path, `slackCallbackRoutes` as children
// of the `/callback` parent — which is the same shape every other feature's route barrel uses.
export {
  integrationCallbackRoutes,
  sheetsAddonRoutes,
  slackCallbackRoutes,
  slackLinkRoutes,
} from './routes'
export { SlackLinkApi, SLACK_LINK_COMPLETE_PATH } from './api/SlackLinkApi'
export type {
  SlackInstallResult,
  SlackInstallStatus,
  SlackLinkCompletion,
} from './models/slackLink.types'

// Provider callback cutover (ORP-18695). The app composition root passes its query registrar;
// features never import app/router or app/shell.
export { registerIntegrationQueryCallbacks } from './flows/queryCallbacks'
export type {
  IntegrationQueryCallbackOptions,
  IntegrationQueryCallbackRuntime,
  IntegrationQueryRegister,
} from './flows/queryCallbacks'
export { useIntegrationCallbackRuntime } from './composables/useIntegrationCallbackRuntime'
export type { IntegrationCallbackTranslator } from './composables/useIntegrationCallbackRuntime'
export { completeShopifyInstall } from './service/completeShopifyInstall'
export type {
  CallbackNotice,
  ShopifyCompletionCommand,
  ShopifyCompletionDto,
} from './models/callback.types'
